import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { User, Lock, Shield, CheckCircle, Eye, EyeOff, QrCode } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ProfilePage = () => {
  const { user, authAxios, refreshUser } = useAuth();
  const [formData, setFormData] = useState({ name: "", company: "", phone: "" });
  const [pwData, setPwData] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [twoFaSetup, setTwoFaSetup] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [settingUp2FA, setSettingUp2FA] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({ name: user.name || "", company: user.company || "", phone: user.phone || "" });
    }
  }, [user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authAxios.patch("/auth/profile", formData);
      await refreshUser();
      toast.success("Perfil actualizado");
    } catch (error) { toast.error(error.response?.data?.detail || "Error al actualizar"); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwData.new_password !== pwData.confirm_password) { toast.error("Las contraseñas no coinciden"); return; }
    if (pwData.new_password.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    setChangingPw(true);
    try {
      await authAxios.post("/auth/change-password", { current_password: pwData.current_password, new_password: pwData.new_password });
      toast.success("Contraseña cambiada exitosamente");
      setPwData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) { toast.error(error.response?.data?.detail || "Error al cambiar contraseña"); }
    finally { setChangingPw(false); }
  };

  const handleSetup2FA = async () => {
    setSettingUp2FA(true);
    try {
      const response = await authAxios.post("/auth/2fa/setup");
      setTwoFaSetup(response.data);
    } catch (error) { toast.error("Error al configurar 2FA"); }
    finally { setSettingUp2FA(false); }
  };

  const handleVerify2FA = async () => {
    try {
      await authAxios.post("/auth/2fa/verify", { code: twoFaCode });
      toast.success("2FA activado exitosamente");
      setTwoFaSetup(null);
      setTwoFaCode("");
      await refreshUser();
    } catch (error) { toast.error(error.response?.data?.detail || "Código inválido"); }
  };

  const handleDisable2FA = async () => {
    if (!twoFaCode) { toast.error("Ingresa tu código 2FA para desactivar"); return; }
    try {
      await authAxios.post("/auth/2fa/disable", { code: twoFaCode });
      toast.success("2FA desactivado");
      setTwoFaCode("");
      await refreshUser();
    } catch (error) { toast.error(error.response?.data?.detail || "Código inválido"); }
  };

  if (!user) return <Navigate to="/login" />;

  const tabs = [
    { id: "profile", label: "Perfil", icon: User },
    { id: "security", label: "Seguridad", icon: Lock },
    { id: "2fa", label: "2FA", icon: Shield },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#5D4037]">Mi Perfil</h1>
          <p className="text-stone-600">{user.email}</p>
        </div>

        <div className="flex gap-2 mb-6">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all ${activeTab === tab.id ? "bg-[#2E7D32] text-white" : "bg-white text-stone-600 hover:bg-stone-100 border border-stone-200"}`} data-testid={`tab-${tab.id}`}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "profile" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bento-card p-8">
            <h2 className="text-xl font-bold text-[#5D4037] mb-6">Información Personal</h2>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Nombre</label><input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" data-testid="profile-name" /></div>
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Empresa</label><input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" data-testid="profile-company" /></div>
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Teléfono</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" data-testid="profile-phone" /></div>
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Email</label><input type="email" value={user.email} disabled className="w-full h-12 px-4 rounded-xl border border-stone-200 bg-stone-50 text-stone-500 outline-none" /></div>
              <button type="submit" disabled={saving} className="btn-primary h-12 w-full" data-testid="save-profile-btn">{saving ? "Guardando..." : "Guardar Cambios"}</button>
            </form>
          </motion.div>
        )}

        {activeTab === "security" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bento-card p-8">
            <h2 className="text-xl font-bold text-[#5D4037] mb-6">Cambiar Contraseña</h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Contraseña actual</label><input type={showPw ? "text" : "password"} value={pwData.current_password} onChange={(e) => setPwData({...pwData, current_password: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" required data-testid="current-pw" /></div>
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Nueva contraseña</label><input type={showPw ? "text" : "password"} value={pwData.new_password} onChange={(e) => setPwData({...pwData, new_password: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" required data-testid="new-pw" /></div>
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Confirmar nueva contraseña</label><input type={showPw ? "text" : "password"} value={pwData.confirm_password} onChange={(e) => setPwData({...pwData, confirm_password: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" required data-testid="confirm-pw" /></div>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-stone-600"><input type="checkbox" checked={showPw} onChange={() => setShowPw(!showPw)} /> Mostrar contraseñas</label>
              <button type="submit" disabled={changingPw} className="btn-primary h-12 w-full" data-testid="change-pw-btn">{changingPw ? "Cambiando..." : "Cambiar Contraseña"}</button>
            </form>
          </motion.div>
        )}

        {activeTab === "2fa" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bento-card p-8">
            <h2 className="text-xl font-bold text-[#5D4037] mb-2">Autenticación de Dos Factores</h2>
            <p className="text-stone-600 mb-6">Agrega una capa extra de seguridad a tu cuenta usando una app como Google Authenticator.</p>

            {user.totp_enabled ? (
              <div>
                <div className="flex items-center gap-2 mb-6 text-green-600"><CheckCircle size={20} /><span className="font-medium">2FA está activado</span></div>
                <div className="space-y-4">
                  <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Código para desactivar</label><input type="text" value={twoFaCode} onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} placeholder="123456" className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none font-mono text-center text-xl tracking-widest" data-testid="disable-2fa-code" /></div>
                  <button onClick={handleDisable2FA} className="w-full h-12 border-2 border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50" data-testid="disable-2fa-btn">Desactivar 2FA</button>
                </div>
              </div>
            ) : twoFaSetup ? (
              <div>
                <p className="text-sm text-stone-600 mb-4">Escanea el código QR con tu app de autenticación:</p>
                <div className="flex justify-center mb-4"><img src={`data:image/png;base64,${twoFaSetup.qr_code}`} alt="QR Code" className="w-48 h-48 rounded-xl border" data-testid="qr-code-img" /></div>
                <p className="text-xs text-stone-500 text-center mb-4">O ingresa manualmente: <code className="bg-stone-100 px-2 py-1 rounded text-xs">{twoFaSetup.secret}</code></p>
                <div className="space-y-4">
                  <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Código de verificación</label><input type="text" value={twoFaCode} onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} placeholder="123456" className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none font-mono text-center text-xl tracking-widest" data-testid="verify-2fa-code" /></div>
                  <button onClick={handleVerify2FA} className="btn-primary h-12 w-full" data-testid="verify-2fa-btn">Verificar y Activar</button>
                </div>
              </div>
            ) : (
              <button onClick={handleSetup2FA} disabled={settingUp2FA} className="btn-primary h-12 w-full flex items-center justify-center gap-2" data-testid="setup-2fa-btn">
                <Shield size={18} /> {settingUp2FA ? "Configurando..." : "Configurar 2FA"}
              </button>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
